import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import { Button, Chip, Stack, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import MapDirectionButton from './MapDirectionButton';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// 🔹 Animated FitBounds (smooth fly instead of jump)
function FitBounds({ coordinates }) {
  const map = useMap();

  React.useEffect(() => {
    if (!coordinates || coordinates.length === 0) return;

    if (coordinates.length === 1) {
      // Single point → fly smoothly to it
      const { latitude, longitude } = coordinates[0];
      map.flyTo([latitude, longitude], 10, { duration: 1.5 });
    } else {
      // Multiple points → fit bounds with smooth animation
      const bounds = L.latLngBounds(
        coordinates.map(c => [c.latitude, c.longitude])
      );
      map.flyToBounds(bounds, { padding: [50, 50], duration: 1.5 });
    }
  }, [coordinates, map]);

  return null;
}

const MapView = ({ coordinates }) => {
  const defaultPos = coordinates?.[0] || { latitude: 0, longitude: 0 };

  const shortenAddress = (fullAddress = '') =>
    fullAddress?.split('\n')[0];

  return (
    <div className="rounded overflow-hidden shadow-sm" style={{ height: '100%' }}>
      <MapContainer
        center={[defaultPos.latitude, defaultPos.longitude]}
        zoom={14} // default, overridden by FitBounds
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {/* Auto-adjust zoom + animate */}
        <FitBounds coordinates={coordinates} />

        {Array.isArray(coordinates) && coordinates.map((point, index) => (
          <Marker key={index} position={[point.latitude, point.longitude]}>
            <Popup>
              {point?.issue_id ? (
                <div style={{ width: "220px" }}>
                  <Typography variant="body2" fontWeight={600}>
                    {point.category}
                  </Typography>

                  <Chip
                    label={point.status}
                    color={
                      point.status === "Resolved"
                        ? "success"
                        : point.status === "In Progress"
                        ? "primary"
                        : "warning"
                    }
                    size="small"
                    sx={{ mt: 0.5, mb: 1 }}
                  />

                  <Typography variant="caption" display="block" gutterBottom>
                    📍 {shortenAddress(point.location)}
                  </Typography>

                  <Stack direction="row" spacing={1} mt={1}>
                    <Button
                      component={Link}
                      to={`/issues/${point.issue_id}`}
                      size="small"
                      variant="outlined"
                      startIcon={<InfoIcon />}
                    >
                      Details
                    </Button>

                    <MapDirectionButton
                      latitude={point.latitude}
                      longitude={point.longitude}
                    />
                  </Stack>
                </div>
              ) : (
                <div style={{ width: "200px" }}>
                  {point?.location && (
                    <Typography variant="body2" gutterBottom>
                      📍 {shortenAddress(point.location)}
                    </Typography>
                  )}
                  <Typography variant="caption">
                    Coordinates: [{point.latitude}, {point.longitude}]
                  </Typography>
                </div>
              )}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default MapView;
